import api from './api';

export type SsoProvider = 'teams' | 'maximo' | 'slack' | 'custom-sso';

export interface TeamsConfig {
  clientId: string;
  clientSecret?: string;
  tenantId: string;
  redirectUri: string;
  scopes?: string[];
  captureTeamContext?: boolean;
  captureChannelContext?: boolean;
  allowedDomains?: string[];
}

export interface MaximoConfig {
  authUrl: string;
  apiUrl: string;
  loginType: 'form' | 'oauth' | 'basic';
  clientId?: string;
  clientSecret?: string;
  allowedOrganizations?: string[];
}

export interface SlackConfig {
  clientId: string;
  clientSecret?: string;
  redirectUri: string;
  scopes?: string[];
  captureWorkspaceContext?: boolean;
  captureChannelContext?: boolean;
  allowedWorkspaces?: string[];
}

export interface CustomSsoConfig {
  provider: 'oauth2' | 'saml';
  loginUrl: string;
  logoutUrl?: string;
  clientId?: string;
  clientSecret?: string;
  redirectUri?: string;
  scopes?: string[];
  additionalConfig?: Record<string, any>;
}

export interface SsoIntegration {
  id: string;
  name: string;
  description?: string;
  provider: SsoProvider;
  isActive: boolean;
  config: TeamsConfig | MaximoConfig | SlackConfig | CustomSsoConfig | Record<string, any>;
  lastUsedAt?: string;
  usageCount: number;
  widgetCount?: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface CreateSsoIntegrationRequest {
  name: string;
  description?: string;
  provider: SsoProvider;
  isActive?: boolean;
  config: TeamsConfig | MaximoConfig | SlackConfig | CustomSsoConfig | Record<string, any>;
}

export interface UpdateSsoIntegrationRequest {
  name?: string;
  description?: string;
  provider?: SsoProvider;
  isActive?: boolean;
  config?: TeamsConfig | MaximoConfig | SlackConfig | CustomSsoConfig | Record<string, any>;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  details?: any;
  errors?: string[];
}

// Create a new SSO integration
export const createSsoIntegration = async (
  data: CreateSsoIntegrationRequest,
): Promise<SsoIntegration> => {
  const response = await api.post('/sso-integrations', data);
  return response.data;
};

// Get all SSO integrations
export const getSsoIntegrations = async (provider?: SsoProvider): Promise<SsoIntegration[]> => {
  const response = await api.get('/sso-integrations', {
    params: provider ? { provider } : {},
  });
  return response.data;
};

// Get single SSO integration by ID
export const getSsoIntegrationById = async (id: string): Promise<SsoIntegration> => {
  const response = await api.get(`/sso-integrations/${id}`);
  return response.data;
};

// Update SSO integration
export const updateSsoIntegration = async (
  id: string,
  data: UpdateSsoIntegrationRequest,
): Promise<SsoIntegration> => {
  const response = await api.put(`/sso-integrations/${id}`, data);
  return response.data;
};

// Delete SSO integration
export const deleteSsoIntegration = async (id: string): Promise<{ message: string }> => {
  const response = await api.delete(`/sso-integrations/${id}`);
  return response.data;
};

// Test SSO connection
export const testSsoConnection = async (id: string): Promise<TestConnectionResponse> => {
  const response = await api.post(`/sso-integrations/${id}/test`);
  return response.data;
};
